'use client'

import { useState } from 'react'

import { track } from '@amplitude/analytics-browser'
import { Search } from 'lucide-react'
import useSWR from 'swr'

import { buildDepartUrl } from '@/lib/c2c/build-depart-url'
import type { TripData } from '@/lib/c2c/transforms/schedule'

import { DateSelector, shiftDate } from './date-selector'
import { TripCard } from './trip-card'

/**
 * Amplitude `Trip Selected` payload for a c2c-page trip-card click.
 *
 * c2c is a pre-search surface — the convention's canonical `trips` shape
 * adds `depart_city_code`, `arrive_city_code`, `depart_wid`, `arrive_wid`,
 * `leg_count`, `f_pricing`, and `total_cart_price_usd` from search results
 * onward. Those fields are omitted here because they are not derivable on
 * c2c. The four city/wid codes are encoded inside `trip_id` for post-hoc
 * decoding by analysts. See FR-C2 in
 * .docs/specs/legacy/product/c2c/amplitude-parity-audit/spec.md.
 */
function buildTripSelectedPayload(
    trip: TripData,
    mode: string,
    position: number,
): Record<string, unknown> {
    const tripObject: Record<string, unknown> = {
        trip_id: trip.tripId,
        mode,
        is_routed: trip.transfers > 0,
        transfer_count: trip.transfers,
    }
    if (trip.carrierCode !== undefined) {
        tripObject.carrier_code = trip.carrierCode
    }
    if (trip.price !== undefined) {
        tripObject.ticket_price_usd = Math.round(trip.price * 100) / 100
    }
    return {
        trips: [tripObject],
        results_position: position,
    }
}

interface LocationData {
    placeId: string
    name: string
    lat: number
    lng: number
    wcityid: string
}

interface UpcomingTripsProps {
    title?: string
    origin: LocationData
    destination: LocationData
    initialDate?: string
    locale: string
    mode: string
    initialTrips?: TripData[]
    /** Server-computed anchor date in YYYY-MM-DD format; prevents hydration mismatch from calling new Date() at render time. */
    baseDate?: string
}

async function fetchTrips(url: string, isSeoPage: boolean): Promise<{ trips: TripData[] }> {
    const response = await fetch(`${url}&isSeoPage=${isSeoPage}`)
    if (!response.ok) {
        throw new Error('Failed to fetch trips')
    }
    return response.json() as Promise<{ trips: TripData[] }>
}

function toLocalDateString(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export function UpcomingTrips({
    title,
    origin,
    destination,
    initialDate,
    locale,
    mode,
    initialTrips,
    baseDate,
}: UpcomingTripsProps) {
    const DAYS_TO_SHOW = 7
    const todayLocal = baseDate ?? toLocalDateString(new Date())
    const tomorrow = shiftDate(todayLocal, 1)
    const fixedDates = Array.from({ length: DAYS_TO_SHOW }, (_, i) => shiftDate(tomorrow, i))

    const [selectedDate, setSelectedDate] = useState(() => {
        if (!initialDate || initialDate <= tomorrow) {
            return tomorrow
        }
        return initialDate
    })

    // Track whether user has changed dates — initial load uses isSeoPage=true
    // (cached data, matching dev SSR), subsequent date changes use isSeoPage=false
    // (real-time data, matching dev client-side behavior).
    // isSeoPage is excluded from the SWR key so revisiting a previously-fetched
    // date serves the cached result (matching ui-react's Redux caching behavior).
    const [hasChangedDate, setHasChangedDate] = useState(false)

    function handleDateChange(date: string) {
        if (date === selectedDate) {
            return
        }
        setHasChangedDate(true)
        setSelectedDate(date)
    }

    const params = new URLSearchParams({
        originPlaceId: origin.placeId,
        originName: origin.name,
        originLat: String(origin.lat),
        originLng: String(origin.lng),
        destPlaceId: destination.placeId,
        destName: destination.name,
        destLat: String(destination.lat),
        destLng: String(destination.lng),
        fromId: origin.wcityid,
        toId: destination.wcityid,
        date: selectedDate,
        locale,
        mode,
    })

    const hasRequiredData = origin.placeId && destination.placeId
    const hasServerData = initialTrips !== undefined && !hasChangedDate

    // SWR key is null when using server data — no fetch on mount
    const swrKey = hasServerData
        ? null
        : hasRequiredData
          ? `/api/nexus/schedules?${params.toString()}`
          : null

    const { data, error, isLoading } = useSWR<{ trips: TripData[] }, Error>(
        swrKey,
        (url: string) => fetchTrips(url, !hasChangedDate),
        { revalidateOnFocus: false, revalidateIfStale: false },
    )

    // Upstream failures (e.g. 502 from /api/nexus/schedules) surface as SWR
    // errors. Collapse them into the "no preview" state so the page keeps
    // rendering the empty experience instead of a loud error card.
    const trips = hasServerData ? initialTrips : error ? [] : data?.trips
    const showLoading = !hasServerData && isLoading
    const hasData = hasServerData || data !== undefined || Boolean(error)

    if (!hasRequiredData) {
        return null
    }

    const searchUrl = buildDepartUrl({
        origin: origin.name,
        destination: destination.name,
        date: selectedDate,
        locale,
        query: { dpid: destination.placeId, opid: origin.placeId },
    })

    return (
        <section
            id="schedule"
            data-section="upcoming-trips"
            className="my-[72px] md:mx-auto md:my-24 md:flex md:max-w-[637px] md:flex-col md:justify-center"
        >
            <h2 className="mb-6 text-center text-[20px] font-bold text-[#44474a] md:mb-0 md:text-[32px]">
                {title ?? 'Upcoming Trips'}
            </h2>

            <DateSelector
                dates={fixedDates}
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
            />

            <div className="mt-4 space-y-2">
                {showLoading && (
                    <div className="flex items-center justify-center gap-2 py-16">
                        <span
                            data-testid="loading-dot"
                            className="h-3 w-3 animate-pulse rounded-full bg-[var(--watermelon-500)]"
                        />
                        <span
                            data-testid="loading-dot"
                            className="h-3 w-3 animate-pulse rounded-full bg-[var(--wanderblu-500)]"
                        />
                        <span
                            data-testid="loading-dot"
                            className="h-3 w-3 animate-pulse rounded-full bg-[var(--wanderblu-700)]"
                        />
                    </div>
                )}

                {trips?.map((trip, index) => (
                    <TripCard
                        key={trip.tripId}
                        departTime={trip.departTime}
                        arriveTime={trip.arriveTime}
                        departStation={trip.departStation}
                        arriveStation={trip.arriveStation}
                        duration={trip.duration}
                        price={trip.price}
                        carrierName={trip.carrierName}
                        carrierLogo={trip.carrierLogo}
                        carrierRating={trip.carrierRating}
                        isLuxury={trip.isLuxury}
                        searchUrl={trip.searchUrl}
                        transfers={trip.transfers}
                        mode={mode}
                        onSelect={() => {
                            track('Trip Selected', buildTripSelectedPayload(trip, mode, index))
                        }}
                    />
                ))}

                {trips?.length === 0 && (
                    <div className="flex flex-col items-center py-10 text-center md:mx-auto md:w-1/2">
                        <Search className="mb-6 h-10 w-10 text-[#767676]" />
                        <span className="text-[#767676]">
                            No preview is currently available. Please try another date or search the
                            full schedule.
                        </span>
                    </div>
                )}
            </div>

            {hasData && (
                <a
                    href={searchUrl}
                    rel="nofollow"
                    className="mx-auto mt-6 block w-full rounded-[7px] bg-[#d13b3b] py-3 text-center text-sm font-bold tracking-wide text-white uppercase transition-colors hover:bg-[#b83232] md:mt-12 md:w-[280px]"
                >
                    See Full Schedule
                </a>
            )}
        </section>
    )
}
